import { action, computed, observable, runInAction } from 'mobx'
import type {
  AddTeamMemberFn,
  GetCurrentUserWorkspaceMembershipsFn,
  GetWorkspaceTeamRolesFn,
  GetWorkspaceTeamsFn,
  ListTeamRolesFn,
  ListTeamsFn,
  ListTeamMembersForWorkspaceMemberFn,
} from '@/features/iam'
import { task } from 'mobx-task'
import { func, validationContext } from 'validx'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import { type SearchableUser } from '@/features/iam/users/SearchableUser'
import { type Team } from '@/features/iam/teams/Team'
import { type TeamMember } from '@/features/iam/teams/TeamMember'
import { type TeamRole } from '@/features/iam/teams/TeamRole'
import { type Workspace } from '@/features/iam/workspaces/Workspace'
import { ModalViewModel } from '@/infra/ModalViewModel'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { toStream } from '@/utils/mobx-util'
import { withAbortSignal } from '@/utils/rx-util'
import { distinctUntilChanged, EMPTY, switchMap } from 'rxjs'

export class AddUserToTeamDialogViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  readonly modal = new ModalViewModel({ onClose: () => this.reset() })

  /**
   * The currently selected workspace.
   */
  @observable
  accessor workspace: Workspace | null = null

  /**
   * The currently selected team.
   */
  @observable
  accessor team: Team | null = null

  /**
   * The currently selected team role.
   */
  @observable
  accessor teamRole: TeamRole | null = null

  /**
   * The list of teams available in the selected workspace.
   */
  @observable
  accessor teams: Team[] = []

  /**
   * The list of team roles available within the selected team.
   */
  @observable
  accessor teamRoles: TeamRole[] = []

  /**
   * The list of existing team members for the selected user.
   */
  @observable
  accessor existingTeamMembers: TeamMember[] = []

  /**
   * Validation rules for adding a user to a team.
   */
  readonly validation = validationContext(this, {
    workspace: [
      func(() => this.workspace !== null, 'You must select a workspace.'),
    ],
    team: [func(() => this.team !== null, 'You must select a team.')],
    teamRole: [func(() => this.teamRole !== null, 'You must select a role.')],
  })

  private readonly _options$ = toStream(() => this.workspace, true).pipe(
    distinctUntilChanged(),
    switchMap((workspace) =>
      workspace
        ? withAbortSignal((signal) => this.fetchOptions(workspace, signal))
        : EMPTY,
    ),
  )

  constructor(
    private readonly _user: () => SearchableUser,
    private readonly _listTeams: ListTeamsFn,
    private readonly _getWorkspaceTeams: GetWorkspaceTeamsFn,
    private readonly _listTeamRoles: ListTeamRolesFn,
    private readonly _getWorkspaceTeamRoles: GetWorkspaceTeamRolesFn,
    private readonly _listTeamMembersForWorkspaceMember: ListTeamMembersForWorkspaceMemberFn,
    private readonly _addTeamMember: AddTeamMemberFn,
    private readonly _getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
  ) {
    this._lifecycle.manage(this._options$)
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * The currently selected user.
   */
  @computed
  get user() {
    return this._user()
  }

  /**
   * The list of workspaces the current user (i.e. the admin) may create team members in.
   */
  @computed
  get currentUserWorkspaces() {
    return this._getCurrentUserWorkspaceMemberships()
      .filter((mb) => mb.member.permissions.adminManageTeamMember)
      .map((mb) => mb.workspace)
  }

  /**
   * The list of workspaces available to the currently selected user (i.e. the one being viewed).
   */
  @computed
  get selectedUserWorkspaces() {
    return this.user.memberships.map((mb) => mb.workspace)
  }

  /**
   * The list of workspaces that are available to the currently selected user
   * AND that the current user may create team members in.
   */
  @computed
  get availableWorkspaces() {
    return this.selectedUserWorkspaces.filter((ws) =>
      this.currentUserWorkspaces.includes(ws),
    )
  }

  /**
   * The list of teams that are available to be joined by the currently selected user
   * (i.e. teams that the currently selected user is NOT already a member of).
   */
  @computed
  get availableTeams() {
    return this.teams.filter(
      (team) =>
        !this.existingTeamMembers.some(
          (teamMember) => teamMember.team.id === team.id,
        ),
    )
  }

  /**
   * Set the currently selected workspace.
   */
  @action.bound
  setWorkspace(workspace?: Workspace | null) {
    if (workspace === this.workspace) {
      return
    }

    this.workspace = workspace ?? null
    this.clearSelections()
  }

  /**
   * Set the currently selected team.
   */
  @action.bound
  setTeam(team?: Team | null) {
    this.team = team ?? null
  }

  /**
   * Set the currently selected team role.
   */
  @action.bound
  setTeamRole(teamRole?: TeamRole | null) {
    this.teamRole = teamRole ?? null
  }

  /**
   * Clear the selections and options that belong to the previously selected workspace.
   */
  @action.bound
  clearSelections() {
    this.team = null
    this.teamRole = null
    this.teams = []
    this.teamRoles = []
    this.existingTeamMembers = []
  }

  /**
   * Reset the view model to its default state.
   */
  @action.bound
  reset() {
    this.workspace = null
    this.clearSelections()
    this.validation.reset()
  }

  /**
   * Add the currently selected user to the selected team with the specified team role.
   */
  submit = task.resolved(async () => {
    const { team, teamRole, workspace } = this
    if (
      this.fetchOptions.pending ||
      !this.validation.reset().validate().isValid ||
      !team ||
      !teamRole ||
      !workspace
    ) {
      return
    }

    const teamMember = await this._addTeamMember({
      userPublicId: this.user.publicId,
      teamId: team.id,
      teamRoleId: teamRole.id,
      workspaceId: workspace.id,
    }).catch(
      this._createAlertErrorHandler('Adding the user to the team failed'),
    )

    this._newAlert()
      .withState('success')
      .saying(
        'Success!',
        `Added ${teamMember.member.displayName} to ${teamMember.team.name} with role ${teamMember.teamRole.name}.`,
      )
      .autoDismiss(2500)
      .show()
    this.modal.close()
  })

  /**
   * Opens the dialog with the specified workspace.
   */
  @action.bound
  showForWorkspace(workspace: Workspace) {
    this.reset()
    this.workspace = workspace
    this.modal.show()
  }

  /**
   * Load the teams, team roles and existing team members for the workspace.
   */
  fetchOptions = task.resolved(
    async (workspace: Workspace, signal: AbortSignal) => {
      const workspaceId = workspace.id
      const withRetry = this._createAlertRetryWrapper(
        'Loading teams failed',
        signal,
      )
      const loaded = await withRetry(() =>
        Promise.all([
          this._listTeams({ workspaceId }, signal).then(() =>
            this._listTeamRoles({ workspaceId }, signal),
          ),
          this._listTeamMembersForWorkspaceMember(
            { userPublicId: this.user.publicId, workspaceId },
            signal,
          ),
        ]),
      )
        // The retry alert has reported the failure.
        .catch(() => null)

      if (!loaded) {
        return
      }

      const [, existingTeamMembers] = loaded

      runInAction(() => {
        this.teams = this._getWorkspaceTeams(workspaceId)
        this.teamRoles = this._getWorkspaceTeamRoles(workspaceId)
        this.existingTeamMembers = existingTeamMembers
      })
    },
  )
}
